<mat-card
  class="has-progress-bar"
  [class.default]="!output"
  [class.card-table]="!!output"
  data-balance-summary-panel
>
  <mat-progress-bar
    [mode]="
      (estimateService.estimateLoading$ | async)
        ? 'indeterminate'
        : 'determinate'
    "
  ></mat-progress-bar>
  <mat-card-title fxLayout="row" fxLayoutAlign="space-between center">
    <span>
      {{ 'patient-details.estimate-card.estimate' | translate }}
      <mat-chip
        *ngIf="estimate?.status === ESTIMATE_STATUS.DRAFT"
        color="warn"
        class="ml-2"
        [removable]="false"
      >
        {{ 'common.draft' | translate }}
      </mat-chip>
      <mat-chip
        *ngIf="estimate?.status === ESTIMATE_STATUS.CONFIRMED"
        color="primary"
        class="ml-2"
        [removable]="false"
      >
        {{ 'statuses.estimate.confirmed' | translate }}
      </mat-chip>
      <mat-chip
        *ngIf="estimate?.status === ESTIMATE_STATUS.DELIVERED"
        color="primary"
        class="ml-2"
        [removable]="false"
      >
        {{ 'statuses.estimate.delivered' | translate }}
      </mat-chip>
      <mat-chip
        *ngIf="estimate?.status === ESTIMATE_STATUS.UNDELIVERED"
        color="primary"
        class="ml-2"
        [removable]="false"
      >
        {{ 'statuses.estimate.undelivered' | translate }}
      </mat-chip>
    </span>
    <div fxLayout="row" fxLayoutAlign="space-between center" fxLayoutGap="10px">
      <span
        *ngIf="canShowModifyButton"
        [matTooltip]="
          canModifyEstimate === false
            ? 'This estimate cannot be modified because it has paid or partial invoices.'
            : ''
        "
      >
        <button
          mat-stroked-button
          color="primary"
          [disabled]="
            (estimateService.estimateLoading$ | async) === true ||
            canModifyEstimate === false
          "
          (click)="onModify()"
        >
          <mat-icon color="primary">edit</mat-icon>
          {{ 'common.modify' | translate }}
        </button>
      </span>

      <button
        *ngIf="canShowHistoryButton"
        mat-stroked-button
        (click)="openHistoryDialog()"
      >
        <mat-icon color="primary">history</mat-icon>
        {{ 'common.history' | translate }}
      </button>

      <button
        *ngIf="canDeleteEstimate"
        mmxConfirmClick
        [message]="'estimate.confirm-delete-estimate' | translate"
        mat-stroked-button
        color="warn"
        [disabled]="estimateService.estimateLoading$ | async"
        (confirm)="onDelete()"
      >
        <mat-icon color="warn">delete</mat-icon>
        {{ 'common.void' | translate }}
      </button>
      <button
        [ngClass]="{
          'highlight-button':
            (needsPricing$ | async) &&
            (estimateService.calcAndSaveDisabled$ | async) === false
        }"
        *ngIf="estimate?.status === ESTIMATE_STATUS.DRAFT"
        mat-stroked-button
        color="primary"
        (click)="onCalculate()"
        [disabled]="
          (estimateService.estimateLoading$ | async) ||
          (estimateService.calcAndSaveDisabled$ | async) ||
          (shopper && shopperEstimateService.shopperForm.invalid)
        "
      >
        {{ 'estimate.calculate-and-save' | translate }}
      </button>
      <span
        *ngIf="
          output?.visits?.length && estimate?.status === ESTIMATE_STATUS.DRAFT
        "
        [matTooltip]="output?.pricerStatus === PRICER_STATUS.FAILED ? ('estimate.finalize-pricer-failed-tooltip' | translate) : ''"
      >
        <button
          mat-stroked-button
          color="primary"
          [disabled]="(estimateService.finalizeDisabled$ | async) || output?.pricerStatus === PRICER_STATUS.FAILED"
          (click)="createInvoices()"
        >
          {{ 'estimate.finalize' | translate }}
        </button>
      </span>

      <ng-container *ngIf="shopper && estimateFinalized()">
        <mmx-estimate-send-invoice
          [shopper]="true"
          [estimate]="estimate"
          (sending)="estimateService.estimateLoading$.next($event)"
        ></mmx-estimate-send-invoice>

        <button
          mat-stroked-button
          color="primary"
          [disabled]="estimateService.estimateLoading$ | async"
          (click)="onPrintShopperPDF()"
        >
          <mat-icon color="primary">print</mat-icon>
          {{ 'common.print' | translate }}
        </button>
      </ng-container>

      <mmx-collect-payment-button
        *ngIf="
          !shopper &&
          (clinic$ | async)?.modules?.payment &&
          estimateFinalized() &&
          !embedded
        "
        [estimate]="estimate"
        [patient]="patient"
        [disabled]="estimateService.estimateLoading$ | async"
        (newCharge)="onNewCharge($event)"
      ></mmx-collect-payment-button>

      <ng-container *ngIf="!shopper && estimateFinalized()">
        <mmx-estimate-send-invoice
          [estimate]="estimate"
          [patient]="patient"
          (sending)="estimateService.estimateLoading$.next($event)"
          data-balance-summary-send
        ></mmx-estimate-send-invoice>

        <mmx-estimate-print-invoice-button
          [invoices]="estimate?.invoices"
        ></mmx-estimate-print-invoice-button>
      </ng-container>
    </div>
  </mat-card-title>
  <mat-card-content>
    <div
      *ngIf="output?.pricerErrors?.length"
      class="pricer-error-banner"
      fxLayout="row"
      fxLayoutAlign="start center"
      fxLayoutGap="8px"
    >
      <mat-icon color="warn">warning</mat-icon>
      <span>
        <strong>{{ 'estimate.pricer-error-label' | translate }}</strong>
        {{ output.pricerErrors[0] }}
        <span *ngIf="output.pricerErrors.length > 1">
          {{ 'estimate.pricer-error-more-count' | translate:{ count: output.pricerErrors.length - 1 } }}
        </span>
        — {{ (output.pricerStatus === PRICER_STATUS.FAILED ? 'estimate.pricer-error-banner-failed' : 'estimate.pricer-error-banner-partial') | translate }}
      </span>
    </div>
    <ng-container *ngIf="output; else noOutput">
      <mat-accordion *ngIf="output?.visits?.length" multi class="mt-4">
        <mat-expansion-panel
          *ngFor="let visit of output.visits"
          [expanded]="estimate.status === ESTIMATE_STATUS.CONFIRMED"
        >
          <mat-expansion-panel-header>
            <mat-panel-title class="visit-title">
                Visit {{ visit.visitNumber }}
                <mat-icon
                  *ngIf="visit.pricerError"
                  color="warn"
                  class="ml-1"
                  [matTooltip]="visit.pricerError"
                  style="font-size: 18px; height: 18px; width: 18px;"
                >warning</mat-icon>
              </mat-panel-title>
            <mat-panel-description
              fxLayout="row"
              fxLayoutAlign="space-between center"
              fxLayoutGap="8px"
            >
              <span>
                {{ 'common.total' | translate }}: {{ visit.total | currency }}
                &mdash;
                {{ 'estimate.insurance-responsibility' | translate }}:
                {{ visit.insuranceResponsibility | currency }}
                &mdash;
                {{ 'estimate.patient-responsibility' | translate }}:
                {{ visit.patientsResponsibility | currency }}
                <ng-container *appHasModule="'charityCare'">
                  <ng-container
                    *ngIf="
                      patient?.patientCharityCareStatus?.status ===
                      CHARITY_CARE_STATUS.APPROVED
                    "
                  >
                    &mdash; ({{
                      'charity-care-summary.charity-care' | translate
                    }})
                  </ng-container>
                </ng-container>
              </span>
              <ng-container *ngIf="(clinic$ | async)?.pricer.enable">
                <button
                  mat-icon-button
                  color="primary"
                  *ngIf="hasPBTrace(visit)"
                  (click)="$event.stopPropagation(); pbTrace(visit)"
                  [matTooltip]="
                    'patient-details.estimate-card.advanced.pb-trace-link'
                      | translate
                  "
                >
                  <mat-icon>receipt_long</mat-icon>
                </button>
                <button
                  mat-icon-button
                  color="primary"
                  *ngIf="hasHBTrace(visit)"
                  (click)="$event.stopPropagation(); hbTrace(visit)"
                  [matTooltip]="
                    'patient-details.estimate-card.advanced.hb-trace-link'
                      | translate
                  "
                >
                  <mat-icon>receipt_long</mat-icon>
                </button>
              </ng-container>
            </mat-panel-description>
          </mat-expansion-panel-header>
          <ng-template matExpansionPanelContent class="p-3">
            <div fxLayout="row" fxLayoutAlign="space-between center">
              <span
                >{{ 'estimate.visit' | translate }}
                {{ visit.visitNumber }} Total</span
              >
              <span>{{ visit.total | currency }}</span>
            </div>
            <div
              fxLayout="row"
              fxLayoutAlign="space-between center"
              class="mt-3"
            >
              <span>{{ 'estimate.deductible-applied' | translate }}</span>
              <span>{{ visit.deductibleApplied | currency }}</span>
            </div>
            <div fxLayout="row" fxLayoutAlign="space-between center">
              <span>{{ 'estimate.copay' | translate }}</span>
              <span>{{ visit.copaymentApplied | currency }}</span>
            </div>
            <div fxLayout="row" fxLayoutAlign="space-between center">
              <span>{{ 'estimate.coinsurance' | translate }}</span>
              <span>{{ visit.coinsuranceApplied | currency }}</span>
            </div>
            <div fxLayout="row" fxLayoutAlign="space-between center">
              <span>{{ 'estimate.out-of-pocket-max' | translate }}</span>
              <span>
                <ng-container *ngIf="visit.outOfPocketMaxApplied != null">
                  {{ visit.outOfPocketMaxApplied | currency }}
                </ng-container>
                <ng-container *ngIf="visit.outOfPocketMaxApplied == null">
                  {{ 'common.not-available' | translate }}
                </ng-container>
              </span>
            </div>
            <div
              fxLayout="row"
              fxLayoutAlign="space-between center"
              *ngIf="visit.discountPercentage > 0"
            >
              <span
                >{{ 'estimate.discount' | translate }} ({{
                  visit.discountPercentage
                }}%)</span
              >
              <span>{{ visit.discountApplied | currency }}</span>
            </div>
            <div
              fxLayout="row"
              fxLayoutAlign="space-between center"
              class="mt-3"
            >
              <strong>{{
                'patient-details.estimate-card.table-two.primary-insurance-pays'
                  | translate
              }}</strong>
              <strong>{{ visit.insuranceResponsibility | currency }}</strong>
            </div>
            <div fxLayout="row" fxLayoutAlign="space-between center">
              <strong>{{
                'patient-details.estimate-card.table-two.secondary-insurance-pays'
                  | translate
              }}</strong>
              <strong>{{
                visit.secondaryInsuranceResponsibility | currency
              }}</strong>
            </div>
            <div fxLayout="row" fxLayoutAlign="space-between center">
              <strong>{{
                'patient-details.estimate-card.patient-responsibility'
                  | translate
              }}</strong>
              <strong>{{ visit.patientsResponsibility | currency }}</strong>
            </div>
            <mat-divider class="mb-3"></mat-divider>
            <span class="text-secondary uppercase text-xs">{{
              'estimate.charges' | translate | uppercase
            }}</span>
            <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="8px">
              <span fxFlex="80px">{{ 'estimate.code' | translate }}</span>
              <span fxFlex>{{
                'estimate.charge-description' | translate
              }}</span>
              <span fxFlex="60px" class="text-right">{{
                'estimate.units' | translate
              }}</span>
              <span fxFlex="80px" class="text-right">{{
                'estimate.unit-amount' | translate
              }}</span>
              <span fxFlex="60px" class="text-right">{{
                'estimate.discount' | translate
              }}</span>
            </div>
            <div
              *ngFor="let charge of visit.charges"
              fxLayout="row"
              fxLayoutAlign="start center"
              fxLayoutGap="8px"
            >
              <span fxFlex="80px">{{
                charge.cptCode || charge.hcpcsCode || charge.chargeCode
              }}</span>
              <span fxFlex>{{ charge.description?.trim() }}</span>
              <mat-icon
                *ngIf="charge.pricerError"
                color="warn"
                style="font-size: 16px; height: 16px; width: 16px; vertical-align: middle;"
                [matTooltip]="charge.pricerError"
              >warning</mat-icon>
              <span fxFlex="60px" class="text-right">{{
                charge.quantity
              }}</span>
              <span
                fxFlex="80px"
                class="text-right"
                [class.text-warn]="isInsuranceResponsibilityCharge(charge)"
                [matTooltip]="
                  isInsuranceResponsibilityCharge(charge)
                    ? ('patient-details.estimate-card.modifier-33-tooltip'
                      | translate)
                    : ''
                "
                [matTooltipDisabled]="!isInsuranceResponsibilityCharge(charge)"
              >
                {{ charge.unitCharge | currency }}
              </span>
              <span fxFlex="80px" class="text-right">
                @if (charge.cptDiscountApplied) {
                {{ charge.cptDiscountApplied | currency }}
                }
              </span>
            </div>
          </ng-template>
        </mat-expansion-panel>
      </mat-accordion>
    </ng-container>
    <ng-template #noOutput>
      {{ 'estimate.click-calculate-and-save' | translate }}
    </ng-template>
  </mat-card-content>
  <mat-card-footer *ngIf="estimate && output">
    <div fxLayout="column">
      <ng-container *appHasModule="'charityCare'">
        <div
          *ngIf="
            patient?.patientCharityCareStatus?.status ===
            CHARITY_CARE_STATUS.APPROVED
          "
          class="charity"
          fxLayoutAlign="center"
        >
          <span>
            {{ 'charity-care-summary.applied' | translate }}
          </span>
        </div>
      </ng-container>
      <div>
        <div fxFlex="50" *ngIf="estimate.inputs.insuranceType !== 'selfpay'">
          <div class="insurance" fxLayout fxLayoutAlign="space-between end">
            <span>{{
              'patient-details.estimate-card.insurance-responsibility'
                | translate
            }}</span>
            <strong>{{
              output.insuranceResponsibility +
                (output.secondaryInsuranceResponsibility || 0) | currency
            }}</strong>
          </div>
        </div>

        <div class="total" fxFlex fxLayout fxLayoutAlign="space-between center">
          <span>{{
            'patient-details.estimate-card.patient-responsibility' | translate
          }}</span>
          <strong>{{ output.patientsResponsibility | currency }}</strong>
        </div>
      </div>
    </div>
  </mat-card-footer>
</mat-card>
